import i18n from 'i18next';
import { call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import {
    getOperationParticipants,
    setError,
    setNotif,
    setPresignedBillingLogo,
    updateBillingEntity,
} from '../../redux/actions';
import {
    GET_PRESIGNED_BILLING_LOGO,
    IMPORT_PARTICIPANTS,
    UPDATE_PARTICIPANT,
} from '../../redux/reducers/constants';
import { selectSelectedOperation } from '../../redux/selectors/operationSelectors';
import { errorHandler } from '../common/errorHandler';

function* updateParticipant(action) {
    try {
        const { participant, newParticipant, formData } = action;
        const operation = yield select(selectSelectedOperation);
        const participantGateway = yield getContext('participantGateway');
        let billingLogoPath;
        if (formData) {
            billingLogoPath = yield call(
                participantGateway.uploadBillingLogo,
                participant,
                formData
            );
        }
        const createdParticipant = yield call(
            participantGateway.updateParticipant,
            participant,
            newParticipant,
            billingLogoPath,
            operation
        );
        if (createdParticipant && formData) {
            yield put(
                updateBillingEntity(createdParticipant.billingEntity, formData)
            );
        }
        //TODO separate success from errors
        yield put(
            setError({
                message: i18n.t(
                    'enoapp.administrative.participants.successfulEdit'
                ),
                variant: 'success',
            })
        );
        yield put(getOperationParticipants(operation));
    } catch (error) {
        yield errorHandler(error, false);
        let messageArg = 'enoapp.administrative.participants.snackErr1';
        let params = {};
        if (
            error?.response?.data.message ===
            'user.email_not_found_for_organizer'
        ) {
            messageArg =
                'enoapp.administrative.participants.emailNotFoundForOrganizer';
            params = {
                email: action.newParticipant.user?.email,
            };
        }
        if (error?.response?.data.message === 'user.email_already_used') {
            messageArg = 'enoapp.administrative.participants.emailAlreadyUsed';
        }
        if (
            error?.response?.data.message ===
            'user.email_update_unauthorized_user_multi_enogrid_product'
        ) {
            messageArg =
                'enoapp.administrative.participants.emailUpdateUnauthorizedMultiProduct';
        }
        yield put(
            setError({
                status: error?.response?.status,
                message: i18n.t(messageArg, params),
                variant: 'error',
            })
        );
        console.error(error);
    }
}

function* getPresignedBillingLogo(action) {
    try {
        const { participant } = action;
        const participantGateway = yield getContext('participantGateway');
        const presignedBillingLogo = yield call(
            participantGateway.getPresignedBillingLogo,
            participant
        );
        yield put(setPresignedBillingLogo(presignedBillingLogo));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* importOperationParticipants(action) {
    const { operation, file } = action;

    const participantGateway = yield getContext('participantGateway');
    try {
        yield put(
            setNotif({
                message: i18n.t(
                    'enoapp.administrative.participants.import.inProgress'
                ),
            })
        );
        const importResults = yield call(
            participantGateway.importParticipants,
            operation,
            file
        );
        if (importResults.errors.length === 0) {
            yield put(
                setNotif({
                    message: i18n.t(
                        'enoapp.administrative.participants.import.done'
                    ),
                })
            );
        } else if (importResults.success.length > 0) {
            yield put(
                setNotif({
                    message: i18n.t(
                        'enoapp.administrative.participants.import.partial'
                    ),
                    variant: 'warning',
                    persist: true,
                })
            );
        }

        if (importResults.errors.length > 0) {
            let errorMessages = [];
            for (let error of importResults.errors) {
                // I18N error if possible
                const i18nErrorMessage = error.message.includes(' ')
                    ? error.message
                    : i18n.t('error.' + error.message);

                errorMessages.push(
                    i18n.t(
                        'enoapp.administrative.participants.import.errorLine',
                        {
                            lineNumber: error.row,
                        }
                    ) +
                        i18nErrorMessage +
                        ' ' +
                        i18n.t(
                            'enoapp.administrative.participants.participantCouldNotBeCreated',
                            {
                                dsoId:
                                    error.data['PRM'] ||
                                    error.data['prm'] ||
                                    error.data['ean'] ||
                                    error.data['EAN'],
                            }
                        )
                );
            }
            yield put(
                setError({
                    message: errorMessages.join('\n'),
                    persist: true,
                })
            );
        }
        yield put(getOperationParticipants(operation));
    } catch (error) {
        yield errorHandler(error);
    }
}

export function* updateParticipantSaga() {
    yield takeLatest(UPDATE_PARTICIPANT, updateParticipant);
}

export function* getPresignedBillingLogoSaga() {
    yield takeLatest(GET_PRESIGNED_BILLING_LOGO, getPresignedBillingLogo);
}

function* importOperationParticipantsSaga() {
    yield takeLatest(IMPORT_PARTICIPANTS, importOperationParticipants);
}

const participantSagas = [
    updateParticipantSaga,
    getPresignedBillingLogoSaga,
    importOperationParticipantsSaga,
];

export default participantSagas;
